import React, { useState, useEffect } from "react";
import useMembreAuth from "../../hooks/useMembreAuth";
import { Camera, Trash2, Mail, Phone, User, Calendar } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { uploadAvatar } from "../../utils/avatarUpload";

export default function MembreProfile() {
  const { user, loading } = useMembreAuth();

  const [avatar, setAvatar] = useState("/images/default-avatar.png");
  const [photoLoading, setPhotoLoading] = useState(false);

  useEffect(() => {
    if (!user) return;

    setAvatar(user.avatar || "/images/default-avatar.png");
  }, [user]);

  if (loading) {
    return <p className="p-6 text-center">Chargement…</p>;
  }

  if (!user) return null;

  async function handleUpload(e) {
    const file = e.target.files[0];

    if (!file) return;

    try {
      setPhotoLoading(true);

      const token = localStorage.getItem("membreToken");

      const url = await uploadAvatar(file, token);

      setAvatar(url);

      const membre = JSON.parse(localStorage.getItem("membre"));

      if (membre) {
        membre.avatar = url;
        localStorage.setItem("membre", JSON.stringify(membre));
      }

      alert("✅ Photo mise à jour avec succès.");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setPhotoLoading(false);
    }
  }

  async function deletePhoto() {
    if (!window.confirm("Supprimer la photo ?")) return;

    try {
      const token = localStorage.getItem("membreToken");

      const res = await fetch("/api/membre?action=deleteAvatar", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.success) {
        setAvatar("/images/default-avatar.png");
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-extrabold mb-6 text-center">Mon Profil</h1>

        <div className="bg-white rounded-3xl shadow-xl p-6">
          <div className="flex flex-col items-center">
            <div className="relative">
              <img
                src={avatar}
                className="w-36 h-36 rounded-full object-cover border-4 border-blue-600"
                alt="avatar"
              />

              {avatar === "/images/default-avatar.png" ? (
                <>
                  <input
                    type="file"
                    id="uploadPhoto"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUpload}
                  />

                  <button
                    onClick={() =>
                      document.getElementById("uploadPhoto").click()
                    }
                    className="absolute bottom-1 right-1 bg-blue-600 text-white p-2 rounded-full"
                    disabled={photoLoading}
                  >
                    <Camera size={18} />
                  </button>
                </>
              ) : (
                <button
                  onClick={deletePhoto}
                  className="absolute bottom-1 right-1 bg-red-600 text-white p-2 rounded-full"
                  disabled={photoLoading}
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>

            <h2 className="text-2xl font-bold mt-4">
              {user.prenom.toUpperCase()} {user.nom.toUpperCase()}
            </h2>
            <p className="text-blue-600 font-semibold text-sm">
              ID : {user.member_id}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Info icon={<Mail />} label="Email" value={user.email} />
            <Info
              icon={<Phone />}
              label="Téléphone"
              value={user.telephone || "—"}
            />
            <Info
              icon={<Calendar />}
              label="Date de naissance"
              value={
                user.birthday
                  ? new Date(user.birthday).toLocaleDateString("fr-FR")
                  : "—"
              }
            />
            <Info
              icon={<User  />}
              label="Fonction"
              value={user.fonction || "Membre Actif"}
            />
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => (window.location.href = "/membre/change-password")}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold"
            >
              Modifier mon mot de passe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon, label, value }) {
  return (
    <div className="bg-gray-50 p-4 rounded-xl flex items-center gap-3">
      <div className="text-blue-600">{icon}</div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-semibold">{value}</p>
      </div>
    </div>
  );
}
